import { Button } from "@/components/ui/button";
import { Menu, Moon, Sun, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";

export const Header = () => {
  const [isDark, setIsDark] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const closeMenu = useCallback(
    (restoreFocus = true) => {
      setIsMenuOpen(false);
      if (restoreFocus) {
        menuButtonRef.current?.focus();
      }
    },
    [menuButtonRef],
  );

  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [isDark]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [closeMenu, isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isMenuOpen]);

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Dashboard", href: "/dashboard" },
    { name: "Wizard", href: "/wizard", featured: true },
    { name: "Downloads", href: "/downloads" },
    { name: "Models", href: "/models" },
    { name: "Sizing", href: "/sizing" },
    { name: "Cost Simulator", href: "/cost-simulator" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 max-w-screen-2xl items-center justify-between">
        {/* Logo & Brand */}
        <div className="flex items-center space-x-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">AI</span>
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">GenAI Inference Hub</span>
              <span className="text-xs text-muted-foreground">Powered by Intel & Dell</span>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav data-tour="navigation" className="hidden md:flex items-center space-x-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={
                item.featured
                  ? "px-4 py-2 text-sm font-semibold bg-gradient-primary text-white rounded-lg hover:shadow-glow transition-smooth"
                  : "px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth rounded-lg hover:bg-muted"
              }
            >
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsDark(!isDark)}
            className="h-9 w-9 p-0"
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="hidden sm:inline-flex"
          >
            Contact Sales
          </Button>
          
          <Button
            size="sm"
            className="bg-gradient-primary hover:shadow-glow transition-smooth"
          >
            Get Started
          </Button>
          
          {/* Mobile menu trigger */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden h-9 w-9 p-0"
            onClick={openMenu}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            aria-haspopup="dialog"
            ref={menuButtonRef}
          >
            <Menu className="h-4 w-4" />
            <span className="sr-only">Open navigation menu</span>
          </Button>
        </div>
      </div>

      {isMenuOpen ? (
        <div
          className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm md:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-navigation-title"
        >
          <div className="flex h-16 items-center justify-between border-b px-6">
            <span
              id="mobile-navigation-title"
              className="text-base font-semibold text-foreground"
            >
              Menu
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => closeMenu()}
              ref={closeButtonRef}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close navigation menu</span>
            </Button>
          </div>
          <nav
            id="mobile-navigation"
            className="flex h-[calc(100%-4rem)] flex-col space-y-1 overflow-y-auto px-6 py-4"
          >
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block rounded-lg px-4 py-3 text-base font-medium text-foreground transition-smooth hover:bg-muted"
                onClick={() => closeMenu(false)}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
};